/** Fixed format: 20220131 */
export const formatDateToString = (dte: Date, sep: string = ''): string => {
  const yyyy = dte.getFullYear().toString();
  const mm = (dte.getMonth() + 1).toString(); // getMonth() is zero-based
  const dd = dte.getDate().toString();
  return yyyy + sep + (mm[1] ? mm : '0' + mm[0]) + sep + (dd[1] ? dd : '0' + dd[0]);
};

/** Return new date, with time set to 0 */
export const startOfDay = (date: Date): Date => {
  date = new Date(date);
  date.setHours(0, 0, 0, 0);
  return date;
};

/** Return new date, with time set to 23:59:59 */
export const endOfDay = (date: Date): Date => {
  date = new Date(date);
  date.setHours(23, 59, 59, 999);
  return date;
};

/** Return new date representing the start of the first day of the month */
export const startOfMonth = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), 1);

/** Return new date representing the the first day of the date's quarter */
export const startOfQuarter = (date: Date): Date => {
  const q = getQuarter(date);
  const month = q === 1 ? 0 : q === 2 ? 3 : q === 3 ? 6 : 9;
  return new Date(date.getFullYear(), month, 1);
};

/** Return new date representing the the last day of the date's quarter */
export const endOfQuarter = (date: Date): Date => {
  const q = getQuarter(date);
  const month = q === 1 ? 3 : q === 2 ? 6 : q === 3 ? 9 : 12;
  return addDays(new Date(date.getFullYear(), month, 1), -1);
};

/** Return quarter number 1,2,3, or 4 */
export const getQuarter = (date: Date): number => {
  const month = date.getMonth();
  const q = month < 3 ? 1 : month < 6 ? 2 : month < 9 ? 3 : 4;
  return q;
};

/** Return quarter name, e.g. "2023 Q1" */
export const formatQuarter = (date: Date): string => {
  const q = getQuarter(date);
  return `${date.getFullYear()} Q${q}`;
};

/** Return new date representing the end of the last day of the month */
export const endOfMonth = (date: Date): Date => {
  date = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  date.setHours(23, 59, 59, 999);
  return date;
};

/** Return the date of the first Monday on or before the given date */
export const startOfWeek = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() - (date.getDay() || 7) + 1);

export const endOfWeek = (date: Date): Date => addWeek(startOfWeek(date));

/** Add a the given number of days to the given date */
export const addDays = (date: Date, days: number): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

/** Add a week, minus one second, to the given date */
export const addWeek = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + 7, date.getHours(), date.getMinutes(), -1);

/** Return true if year, month, and day are the same */
export const isSameDay = (date1: Date, date2: Date): boolean =>
  date1 &&
  date2 &&
  date1.getFullYear() === date2.getFullYear() &&
  date1.getMonth() === date2.getMonth() &&
  date1.getDate() === date2.getDate();

/** Difference between two dates, in days */
export const dateDiff = (date1: Date, date2: Date): number => (date2.getTime() - date1.getTime()) / MILLIS_PER_DAY;
/** Difference between two dates, in whole days */
export const dateDiff2 = (date1: Date, date2: Date): number => (startOfDay(date2).getTime() - startOfDay(date1).getTime()) / MILLIS_PER_DAY;

const MILLIS_PER_DAY = 86400 * 1000;
const MILLIS_PER_WEEK = MILLIS_PER_DAY * 7;

const AU = 'en-AU';

/* eslint-disable @typescript-eslint/naming-convention */
/** Locale-aware format: 31/1/2022 */
export const DateOnly = Intl.DateTimeFormat([AU], {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
});

/** Locale-aware format: 31/01/2022 */
export const DateOnly2 = Intl.DateTimeFormat([AU], {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Locale-aware format: 31/01/22 */
export const DateOnly3 = Intl.DateTimeFormat([AU], {
  year: '2-digit',
  month: '2-digit',
  day: '2-digit',
});

/** Long month name: January */
export const MonthName = Intl.DateTimeFormat([AU], {
  month: 'long',
});

/** Locale-aware format: Mon 31/1 */
export const WeekdayDate = Intl.DateTimeFormat([AU], {
  weekday: 'short',
  month: 'numeric',
  day: 'numeric',
});

/** Locale-aware format: 1:15 PM */
export const TimeOnly = Intl.DateTimeFormat([AU], {
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
});

/** Locale-aware format: 01:15:33 PM */
export const TimeOnly2 = Intl.DateTimeFormat([AU], {
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
});

/** Locale-aware format: 01:15 PM */
export const TimeOnly3 = Intl.DateTimeFormat([AU], {
  hour: 'numeric',
  minute: '2-digit',
  // second: '2-digit',
});

/** Locale-aware format: 31/1/2022, 1:15 PM */
export const DateTime = Intl.DateTimeFormat([AU], {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
});

/** Locale-aware format: 31/01/2022, 01:15 PM */
export const DateTime2 = Intl.DateTimeFormat([AU], {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

/** Locale name: 'en-AU' */
export const currentLocale = DateOnly.resolvedOptions().locale;
/** Timezone name: 'Australia/Perth' */
export const currentTimeZone = DateOnly.resolvedOptions().timeZone;
